import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { movies } from "@/mocks/movies";
import { genreOptions, regionOptions, yearOptions } from "@/mocks/categories";
import { getCategoryBySlug, searchContent } from "@/utils/movieData";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import CategoryFilterBar from "./components/CategoryFilterBar";
import type { Movie } from "@/types/movie";

type SortKey = "default" | "latest" | "heat" | "rating";

const PAGE_SIZE = 8;

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("q")?.trim() ?? "";
  const config = getCategoryBySlug(type);

  const [genre, setGenre] = useState<string[]>([]);
  const [region, setRegion] = useState<string[]>([]);
  const [year, setYear] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>("default");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useDocumentMeta({
    title: keyword
      ? `“${keyword}”的搜索结果 - 秋霞电影`
      : `秋霞电影 - ${config.title}与资讯`,
    description: keyword
      ? `秋霞电影为你找到与“${keyword}”相关的影片与影视内容，快速定位想看的好电影。`
      : config.description,
    keywords: `秋霞电影,${config.label}电影,电影推荐,影片${config.label}`,
  });

  // reset on route change
  useEffect(() => {
    setGenre(config.kind === "genre" && config.genre ? [config.genre] : []);
    setRegion([]);
    setYear([]);
    setSort(config.kind === "heat" ? "heat" : config.kind === "latest" ? "latest" : "default");
    setPage(1);
    setFilterOpen(false);
  }, [config.slug, config.kind, config.genre]);

  useEffect(() => {
    setPage(1);
  }, [genre, region, year, sort]);

  const baseList: Movie[] = useMemo(() => {
    if (keyword) {
      return searchContent(keyword).movies;
    }
    if (config.kind === "classic") {
      return movies.filter((m) => m.rating >= 8.5);
    }
    return movies;
  }, [keyword, config.kind]);

  const filtered = useMemo(() => {
    let list = baseList.filter((m) => {
      const okGenre = genre.length === 0 || m.genres.some((g) => genre.includes(g));
      const okRegion = region.length === 0 || region.includes(m.region);
      const okYear = year.length === 0 || year.includes(String(m.year));
      return okGenre && okRegion && okYear;
    });
    list = [...list];
    if (sort === "latest") list.sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1));
    else if (sort === "heat") list.sort((a, b) => b.heat - a.heat);
    else if (sort === "rating") list.sort((a, b) => b.rating - a.rating);
    else if (config.kind === "heat") list.sort((a, b) => b.heat - a.heat);
    else if (config.kind === "latest") list.sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1));
    else if (config.kind === "classic") list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [baseList, genre, region, year, sort, config.kind]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const resetFilters = () => {
    setGenre(config.kind === "genre" && config.genre ? [config.genre] : []);
    setRegion([]);
    setYear([]);
    setSort("default");
    setPage(1);
  };

  const activeCount = genre.length + region.length + year.length;

  return (
    <div className="bg-background-50">
      {/* page header */}
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-9">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "电影分类" },
              { label: keyword ? `搜索：${keyword}` : config.label },
            ]}
          />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {keyword ? `“${keyword}”的搜索结果` : config.title}
          </h1>
          <p className="mt-2 max-w-[760px] text-sm leading-relaxed text-foreground-600">
            {keyword ? `共找到 ${filtered.length} 部相关影片。` : config.intro}
          </p>
        </div>
      </div>

      <div className="mx-auto flex max-w-[1280px] flex-col gap-6 px-4 py-8 md:px-6 lg:flex-row lg:gap-8">
        {/* sidebar */}
        <aside className="hidden lg:block lg:w-[240px] lg:shrink-0">
          <div className="sticky top-28 rounded-lg border border-background-200 bg-background-50 p-5">
            <CategoryFilterBar
              genre={genre}
              region={region}
              year={year}
              onGenre={setGenre}
              onRegion={setRegion}
              onYear={setYear}
              onReset={resetFilters}
              activeCount={activeCount}
            />
          </div>
        </aside>

        {/* content */}
        <div className="min-w-0 flex-1">
          {/* toolbar */}
          <div className="mb-5 flex items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <span className="hidden sm:inline">共</span>
              <span className="font-semibold text-foreground-950">{filtered.length}</span> 部影片
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setFilterOpen(true)}
                className="flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-800 cursor-pointer lg:hidden"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-filter-3-line"></i></span>
                筛选
                {activeCount > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-500 px-1 text-xs text-background-50">{activeCount}</span>
                )}
              </button>
              <div className="flex items-center gap-1 rounded-md border border-background-300 p-0.5">
                {([
                  { key: "default", label: "综合" },
                  { key: "latest", label: "最新" },
                  { key: "heat", label: "热度" },
                  { key: "rating", label: "评分" },
                ] as { key: SortKey; label: string }[]).map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setSort(s.key)}
                    className={`rounded-md px-2.5 py-1.5 text-sm transition-colors cursor-pointer ${
                      sort === s.key ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {pageItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
              <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-2xl">
                <i className="ri-search-eye-line"></i>
              </span>
              <p className="mt-4 text-base font-medium text-foreground-900">没有找到符合条件的影片</p>
              <p className="mt-1.5 text-sm text-foreground-600">试试更换筛选条件，或重置后再看看</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                重置筛选
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
                {pageItems.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    disabled={current === 1}
                    onClick={() => setPage(current - 1)}
                    className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-s-line"></i></span>
                    上一页
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPage(i + 1)}
                      className={`h-9 w-9 rounded-md text-sm transition-colors cursor-pointer ${
                        current === i + 1
                          ? "bg-primary-500 text-background-50"
                          : "border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-700"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={current === totalPages}
                    onClick={() => setPage(current + 1)}
                    className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                  >
                    下一页
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* mobile filter overlay */}
      {filterOpen && (
        <div className="fixed inset-0 z-[60] flex items-end lg:hidden">
          <div className="absolute inset-0 bg-primary-950/50" onClick={() => setFilterOpen(false)}></div>
          <div className="relative max-h-[80vh] w-full overflow-y-auto rounded-t-2xl bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-foreground-950">筛选与排序</h3>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md border border-background-300 text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="mb-5">
              <p className="mb-2 text-sm font-medium text-foreground-900">排序</p>
              <div className="flex flex-wrap gap-2">
                {([
                  { key: "default", label: "综合" },
                  { key: "latest", label: "最新" },
                  { key: "heat", label: "热度" },
                  { key: "rating", label: "评分" },
                ] as { key: SortKey; label: string }[]).map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setSort(s.key)}
                    className={`rounded-md px-3 py-1.5 text-sm transition-colors cursor-pointer ${
                      sort === s.key ? "bg-primary-500 text-background-50" : "border border-background-300 text-foreground-700"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <CategoryFilterBar
              genre={genre}
              region={region}
              year={year}
              onGenre={setGenre}
              onRegion={setRegion}
              onYear={setYear}
              onReset={resetFilters}
              activeCount={activeCount}
            />
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-5 w-full rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50 cursor-pointer"
            >
              查看 {filtered.length} 部影片
            </button>
          </div>
        </div>
      )}
    </div>
  );
}